import { createAction, createSelector, createSlice } from '@reduxjs/toolkit';

import { requestStatus } from '@clatter/platform';
import { Project, ProjectStep, ProjectStepStatus, ProjectStepTypes, Workbook } from '../../interfaces';

export const PROJECT_STEPPER_FEATURE_KEY = 'projectStepper';

export interface ProjectState {
  steps: ProjectStep[];
  workbooks: Workbook[];
  loadingStatus: string;
  error?: string;
  currentStep: string | null;
  documentsButtonsEnabled: boolean;
}

//region HELPERS
const computeStepsFromProject = ({ projectData }: { projectData: Project }) => {
  const valuesUnion: Record<string, any> = {};
  const requiredFields = new Set<string>();

  //@todo
  const isQueriesStepEnabled = projectData?.ProjectTemplate.Slug.includes('specialty-benefits');
  const isValuesStepEnabled = !isQueriesStepEnabled;

  // valuesUnion will be updated after each patch, so we can safely validate it here.
  const isValuesStepValid = Array.from(requiredFields).every((key) => Boolean(valuesUnion[key]));


  //@todo - validate if values has a proper format
  if (projectData?.Values) {
    for (const valueItem of projectData.Values || []) {
      Object.assign(valuesUnion, valueItem?.data);

      for (const [propertyKey, property] of Object.entries(valueItem?.schema?.properties || {})) {
        const { minLength } = property as { minLength: number };
        if (minLength > 0) {
          requiredFields.add(propertyKey);
        }
      }
    }
  }

  return [
    isValuesStepEnabled && {
      slug: ProjectStepTypes.VALUES_FORM,
      type: ProjectStepTypes.VALUES_FORM,
      displayName: 'Values',
      data: projectData?.Values,
      status: isValuesStepValid ? ProjectStepStatus.VALID : ProjectStepStatus.INITIAL,
      requiredFields: Array.from(requiredFields),
    },
  ].filter(Boolean);
};
//endregion

//region ACTIONS
export const setProjectStepper = createAction<Project>('SET_PROJECT_STEPPER');
export const setDocumentsButtonsEnabled = createAction<boolean>('SET_DOCUMENTS_BUTTONS_ENABLED');

export const setProjectStepStatus =
  createAction<{ slug: string; status: ProjectStepStatus }>('SET_PROJECT_STEP_STATUS');

export const setProjectStepData = createAction<{ slug: string; data: any }>('SET_PROJECT_STEP_DATA');

export const clearProjectSteps = createAction('CLEAR_PROJECT_STEPS');
//endregion

export const selectCurrentStep = createSelector(
  (state: { projectStepper: ProjectState }) => state.projectStepper.steps,
  (_: any, slug: string | null | undefined) => slug,
  (steps: ProjectStep[], slug: string | null | undefined) => {
    if (!slug) {
      console.warn('selectCurrentStep: slug not provided!');
      return null;
    }

    const completeStep: ProjectStep = {
      slug: ProjectStepTypes.COMPLETE,
      type: ProjectStepTypes.COMPLETE,
      displayName: 'Complete',
    };

    if (slug === ProjectStepTypes.COMPLETE) {
      return completeStep;
    }

    return steps.find((step) => step.slug === slug);
  },
);

export const initialProjectStepperState: ProjectState = {
  steps: [],
  currentStep: null,
  workbooks: [],
  loadingStatus: requestStatus.initial,
  error: undefined,
  documentsButtonsEnabled: false,
};

export const projectStepperSlice = createSlice({
  name: PROJECT_STEPPER_FEATURE_KEY,
  initialState: initialProjectStepperState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setProjectStepper, (state, action) => {
      if (!action.payload) {
        return;
      }

      // @ts-ignore
      state.steps = computeStepsFromProject({ projectData: action.payload });
    });
    builder.addCase(setDocumentsButtonsEnabled, (state, action) => {
      state.documentsButtonsEnabled = action.payload;
    });
    builder.addCase(clearProjectSteps, (state, action) => {
      state.steps = [];
    });
    builder.addCase(setProjectStepStatus, (state, action) => {
      if (!action.payload) {
        console.error('Could not set step status, no payload');
        return;
      }

      const { slug, status } = action.payload;

      if (!slug || !status) {
        console.error('Invalid data provided!');
      }

      state.steps = state.steps.map((step) => (step.slug === slug ? { ...step, status: status } : step));
    });
    builder.addCase(setProjectStepData, (state, action) => {
      if (!action.payload) {
        console.error('Could not set step status, no payload');
        return;
      }

      const { slug, data } = action.payload;

      if (!slug || !data) {
        console.error('Invalid data provided!');
      }

      state.steps = state.steps.map((step) =>
        step.slug === slug
          ? {
              ...step,
              data: { ...step.data, ...data },
            }
          : step,
      );
    });
  },
});

export const projectStepperReducer = projectStepperSlice.reducer;

export const getProjectsState = (rootState: any) => rootState[PROJECT_STEPPER_FEATURE_KEY];

export const projectStepperSelectors = {
  loadingStatus: createSelector(getProjectsState, ({ loadingStatus }) => loadingStatus),
  selectDocumentsButtonsEnabled: createSelector(
    getProjectsState,
    ({ documentsButtonsEnabled }) => documentsButtonsEnabled,
  ),
  selectCurrentStep,
};
